import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { IdNameList } from "../../../../../api/services/common/models";
import {
  FormController,
  MuiGrid,
  MuiSelect,
  MuiSelectWithTextInput,
  OptionsDataType,
} from "../../../../../components";
import { MAX_LENGTH_50 } from "../../../../../helpers/constants";
import { setApiDataToDropDownOption } from "../../../../../helpers/utils";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { ProgramSearchProgramFormKey } from "../../../modelsAndSchemas/models";
import { programSearchSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

interface IndividualMoreFieldsProps {
  pageId: string;
  filterDropdownValue: OptionsDataType[];
  dedicatedProjectManagerMappedData: OptionsDataType[];
  clientRelationshipOwnerMappedData: OptionsDataType[];
  lineOfBusinessData: IdNameList[];
  solutionCategories: IdNameList[];
}
export const IndividualMoreFields: FC<IndividualMoreFieldsProps> = ({
  pageId,
  filterDropdownValue,
  dedicatedProjectManagerMappedData,
  clientRelationshipOwnerMappedData,
  lineOfBusinessData,
  solutionCategories,
}) => {
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const { control } = useFormContext();
  const { commonLabels, useFetchLabel, commonValidationMessages } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const getLabel = useFetchLabel(programSearchSiteCoreKeys.searchFormKey);

  return (
    <MuiGrid container className="form-container">
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.billingContactLastName}
        >
          <MuiSelectWithTextInput
            id={`${pageId}-${ProgramSearchProgramFormKey.billingContactLastName}`}
            options={filterDropdownValue}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.billingContactLastNameFilter}`}
            label={getLabel(
              programSearchSiteCoreKeys.billingContactLastNameLabel,
            )}
            control={control}
            placeholder={commonLabels.inputPlaceHolder}
            selectBoxName={
              ProgramSearchProgramFormKey.billingContactLastNameFilter
            }
            requiredARIALabel={commonARIALabel.required}
            maxLength={MAX_LENGTH_50}
            tooltipMessage={commonValidationMessages.fieldSpecialCharactersTooltipMessage}
            tooltipHeaderTitle={commonValidationMessages.allowedSpecialCharactersTitle}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.hrContactLastName}
        >
          <MuiSelectWithTextInput
            id={`${pageId}-${ProgramSearchProgramFormKey.hrContactLastName}`}
            options={filterDropdownValue}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.hrContactLastNameFilter}`}
            placeholder={commonLabels.inputPlaceHolder}
            label={getLabel(programSearchSiteCoreKeys.hRContactLastNameLabel)}
            selectBoxName={ProgramSearchProgramFormKey.hrContactLastNameFilter}
            control={control}
            requiredARIALabel={commonARIALabel.required}
            maxLength={MAX_LENGTH_50}
            tooltipMessage={commonValidationMessages.fieldSpecialCharactersTooltipMessage}
            tooltipHeaderTitle={commonValidationMessages.allowedSpecialCharactersTitle}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.projectManager}
        >
          <MuiSelect
            label={getLabel(programSearchSiteCoreKeys.projectManagerLabel)}
            options={dedicatedProjectManagerMappedData}
            value=""
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramSearchProgramFormKey.projectManager}`}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            showSearch
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.lineOfBusiness}
        >
          <MuiSelect
            label={getLabel(programSearchSiteCoreKeys.productLineLabel)}
            options={setApiDataToDropDownOption(lineOfBusinessData)}
            visibleDefaultSelectOption={true}
            showSearch
            value=""
            id={`${pageId}-${ProgramSearchProgramFormKey.lineOfBusiness}`}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.solutionCategory}
        >
          <MuiSelect
            label={getLabel(programSearchSiteCoreKeys.solutionCategoryLabel)}
            options={setApiDataToDropDownOption(solutionCategories)}
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${ProgramSearchProgramFormKey.solutionCategory}`}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          name={ProgramSearchProgramFormKey.clientRelationshipOwner}
          control={control}
        >
          <MuiSelect
            options={clientRelationshipOwnerMappedData}
            id={`${pageId}-${ProgramSearchProgramFormKey.clientRelationshipOwner}`}
            label={getLabel(
              programSearchSiteCoreKeys.localClientRelationshipOwner,
            )}
            requiredARIALabel={commonARIALabel.required}
            visibleDefaultSelectOption
            value=""
            placeholder={commonLabels.selectPlaceHolder}
            showSearch
          />
        </FormController>
      </MuiGrid>
    </MuiGrid>
  );
};
